import { create } from 'zustand';
import { useAuthStore } from '@features/auth/store/authStore';
import {
  PENDING_INVITE_COUNT_PER_PAGE,
  PENDING_INVITE_FRESHNESS_MS,
} from '@features/user/invitations/constants/pendingInvitations';

type FetchTotalOptions = {
  force?: boolean;
};

type PendingInvitationsState = {
  total: number;
  loading: boolean;
  hasLoaded: boolean;
  lastFetchedAt: number | null;
  lastError: string | null;
  fetchTotal: (options?: FetchTotalOptions) => Promise<void>;
  setTotal: (total: number) => void;
  invalidate: () => Promise<void>;
  reset: () => void;
};

let inflight: Promise<void> | null = null;

export const usePendingInvitationsStore = create<PendingInvitationsState>((set, get) => ({
  total: 0,
  loading: false,
  hasLoaded: false,
  lastFetchedAt: null,
  lastError: null,

  setTotal: (total) =>
    set({
      total: Math.max(0, total),
      hasLoaded: true,
      lastFetchedAt: Date.now(),
      lastError: null,
    }),

  reset: () => {
    inflight = null;
    set({
      total: 0,
      loading: false,
      hasLoaded: false,
      lastFetchedAt: null,
      lastError: null,
    });
  },

  invalidate: async () => {
    await get().fetchTotal({ force: true });
  },

  fetchTotal: async (options = {}) => {
    const { force = false } = options;

    if (!useAuthStore.getState().isAuthenticated()) {
      return;
    }

    const { hasLoaded, lastFetchedAt } = get();
    if (
      !force &&
      hasLoaded &&
      lastFetchedAt != null &&
      Date.now() - lastFetchedAt < PENDING_INVITE_FRESHNESS_MS
    ) {
      return;
    }

    if (inflight) {
      await inflight;
      return;
    }

    set({ loading: true });

    inflight = (async () => {
      try {
        // Dynamic import avoids api → clearClientSessionArtifacts → store → invitationSvc → api cycle.
        const { fetchMyInvitationsAsync } = await import(
          '@features/invitations/services/invitationSvc'
        );
        const response = await fetchMyInvitationsAsync({
          page: 1,
          perPage: PENDING_INVITE_COUNT_PER_PAGE,
        });

        if (response.isSuccess()) {
          const total = response.getData()?.total ?? 0;
          set({
            total,
            hasLoaded: true,
            lastFetchedAt: Date.now(),
            lastError: null,
            loading: false,
          });
        } else {
          set({
            lastError: 'Failed to load pending invitations',
            loading: false,
          });
        }
      } catch {
        set({
          lastError: 'Failed to load pending invitations',
          loading: false,
        });
      } finally {
        inflight = null;
      }
    })();

    await inflight;
  },
}));
